import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { PROJECTILES } from '../../src/game/data.js';
import { cloneGlbPreview, formatGlbTriangles, loadCatalogGlb } from './glbImportUtils.js';
import {
  inspectOneShotSample,
  previewOneShot,
  previewOneShotSample,
} from './proceduralSound.js';

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];

function cleanId(value){
  return String(value||'proyectil-personalizado').normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-').replace(/^-+|-+$/g,'').toLowerCase()
    ||'proyectil-personalizado';
}

function cloneDesign(source={}){
  return {
    style:['dart','twin','bubble','rail'].includes(source.style)?source.style:'dart',
    color:source.color||'#d9dde2',
    accent:source.accent||'#ffcf43',
    scale:Number(source.scale??1),
    spin:Number(source.spin??8),
    modelRotationX:Number(source.modelRotationX??0),
    modelRotationY:Number(source.modelRotationY??0),
    modelRotationZ:Number(source.modelRotationZ??0),
    travelWave:source.travelWave||'triangle',
    travelTone:Number(source.travelTone??230),
    travelDuration:Number(source.travelDuration??.24),
    travelFilter:Number(source.travelFilter??1450),
    travelNoiseGain:Number(source.travelNoiseGain??.018),
    travelToneGain:Number(source.travelToneGain??.025),
    travelSampleVolume:Number(source.travelSampleVolume??.5),
    travelSampleRate:Number(source.travelSampleRate??1),
    impactWave:source.impactWave||'square',
    impactTone:Number(source.impactTone??330),
    impactDuration:Number(source.impactDuration??.09),
    impactFilter:Number(source.impactFilter??1850),
    impactNoiseGain:Number(source.impactNoiseGain??.07),
    impactToneGain:Number(source.impactToneGain??.08),
    impactSampleVolume:Number(source.impactSampleVolume??.5),
    impactSampleRate:Number(source.impactSampleRate??1),
  };
}

function material(color,basic=false){
  return basic
    ?new THREE.MeshBasicMaterial({color})
    :new THREE.MeshPhysicalMaterial({color,metalness:.45,roughness:.3,clearcoat:.5});
}

function createProjectile(design){
  const root=new THREE.Group();
  const main=material(design.color);
  const accent=material(design.accent,design.style==='bubble');
  const sphere=(radius,mat)=>new THREE.Mesh(new THREE.SphereGeometry(radius,20,12),mat);
  const cylinder=(radius,length,mat)=>{
    const mesh=new THREE.Mesh(new THREE.CylinderGeometry(radius*.8,radius,length,16),mat);
    mesh.rotation.x=Math.PI/2;
    return mesh;
  };
  if(design.style==='twin'){
    [-.09,.09].forEach((x)=>{
      const tip=sphere(.075,accent);tip.scale.z=1.4;tip.position.set(x,0,-.1);
      const stem=cylinder(.04,.42,main);stem.position.set(x,0,.16);
      root.add(tip,stem);
    });
  }else if(design.style==='bubble'){
    const orb=sphere(.18,accent);
    const core=sphere(.09,main);
    const ring=new THREE.Mesh(new THREE.TorusGeometry(.22,.025,8,32),main);
    root.add(orb,core,ring);
  }else if(design.style==='rail'){
    const spike=new THREE.Mesh(new THREE.ConeGeometry(.12,.78,8),accent);
    spike.rotation.x=-Math.PI/2;spike.position.z=.05;
    const core=new THREE.Mesh(new THREE.BoxGeometry(.09,.09,.48),main);core.position.z=.3;
    root.add(spike,core);
  }else{
    const tip=sphere(.14,accent);tip.scale.z=1.4;tip.position.z=-.08;
    const stem=cylinder(.055,.55,main);stem.position.z=.22;
    root.add(tip,stem);
  }
  root.scale.setScalar(design.scale);
  root.traverse((child)=>{if(child.isMesh){child.castShadow=true;child.receiveShadow=true;}});
  return root;
}

function dispose(object){
  object?.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry?.dispose();
    (Array.isArray(child.material)?child.material:[child.material]).filter(Boolean).forEach((entry)=>entry.dispose?.());
  });
}

export class ProjectileEditor{
  constructor(screen,callbacks={}){
    this.screen=screen;this.callbacks=callbacks;
    this.canvas=$(screen,'#projectile-editor-canvas');
    this.status=$(screen,'#projectile-editor-message');
    this.fileInput=$(screen,'#projectile-editor-file');
    this.fileName=$(screen,'#projectile-editor-file-name');
    this.fileInfo=$(screen,'#projectile-editor-file-info');
    this.template=$(screen,'#projectile-editor-template');
    this.saveButton=$(screen,'#projectile-editor-save');
    this.soundInputs={
      travel:$(screen,'#projectile-editor-travel-sound-file'),
      impact:$(screen,'#projectile-editor-impact-sound-file'),
    };
    this.soundNames={
      travel:$(screen,'#projectile-editor-travel-sound-file-name'),
      impact:$(screen,'#projectile-editor-impact-sound-file-name'),
    };
    this.soundInfos={
      travel:$(screen,'#projectile-editor-travel-sound-file-info'),
      impact:$(screen,'#projectile-editor-impact-sound-file-info'),
    };
    this.soundButtons={
      travel:$(screen,'#projectile-editor-test-travel'),
      impact:$(screen,'#projectile-editor-test-impact'),
    };
    this.mode='procedural';this.design=cloneDesign(PROJECTILES[0]);this.assetFile=null;this.assetStats=null;
    this.soundModes={travel:'procedural',impact:'procedural'};
    this.soundFiles={travel:null,impact:null};
    this.soundStats={travel:null,impact:null};
    this.initScene();
    $(screen,'#projectile-editor-close').addEventListener('click',()=>this.close());
    this.saveButton.addEventListener('click',()=>this.save());
    $(screen,'#projectile-editor-reset').addEventListener('click',()=>this.reset());
    this.soundButtons.travel.addEventListener('click',()=>this.testSound('travel'));
    this.soundButtons.impact.addEventListener('click',()=>this.testSound('impact'));
    $$ (screen,'[data-projectile-mode]').forEach((button)=>button.addEventListener('click',()=>this.setMode(button.dataset.projectileMode)));
    $$ (screen,'[data-projectile-sound-mode]').forEach((button)=>button.addEventListener('click',()=>{
      const [slot,mode]=button.dataset.projectileSoundMode.split(':');
      this.setSoundMode(slot,mode);
    }));
    $$ (screen,'[data-projectile-design]').forEach((input)=>input.addEventListener('input',()=>{
      const key=input.dataset.projectileDesign;
      this.design[key]=(input.type==='color'||input.tagName==='SELECT')?input.value:Number(input.value);
      input.syncNumberControl?.();
      if(this.mode==='procedural')this.rebuild();
      else if(key==='scale'||key.startsWith('modelRotation'))this.refreshImportedPreview(true);
    }));
    this.template.addEventListener('change',()=>this.reset());
    this.fileInput.addEventListener('change',()=>this.loadGlbFile(this.fileInput.files[0]));
    Object.entries(this.soundInputs).forEach(([slot,input])=>{
      input.addEventListener('change',()=>this.loadSoundFile(slot,input.files[0]));
    });
    window.addEventListener('resize',()=>this.resize());
    this.sync();this.rebuild();this.animate();
  }

  initScene(){
    this.renderer=new THREE.WebGLRenderer({canvas:this.canvas,antialias:true});
    this.renderer.setPixelRatio(Math.min(devicePixelRatio,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled=true;
    this.scene=new THREE.Scene();this.scene.background=new THREE.Color('#17172a');
    this.camera=new THREE.PerspectiveCamera(38,1,.03,40);this.camera.position.set(2.8,1.8,3.8);
    this.controls=new OrbitControls(this.camera,this.canvas);this.controls.enableDamping=true;this.controls.target.set(0,0,.1);
    this.scene.add(new THREE.HemisphereLight(0xe8f3ff,0x25213a,2.2));
    const key=new THREE.DirectionalLight(0xffedd3,4.5);key.position.set(-4,7,-5);key.castShadow=true;this.scene.add(key);
    const floor=new THREE.Mesh(new THREE.CircleGeometry(2.2,48),new THREE.MeshStandardMaterial({color:'#201c34',roughness:.9}));
    floor.rotation.x=-Math.PI/2;floor.position.y=-.42;floor.receiveShadow=true;this.scene.add(floor);
    this.mount=new THREE.Group();this.scene.add(this.mount);
  }

  sync(){
    $$ (this.screen,'[data-projectile-design]').forEach((input)=>{
      input.value=this.design[input.dataset.projectileDesign];input.syncNumberControl?.();
    });
    $$ (this.screen,'[data-projectile-mode]').forEach((button)=>button.classList.toggle('selected',button.dataset.projectileMode===this.mode));
    $(this.screen,'[data-projectile-panel="procedural"]').classList.toggle('hidden',this.mode!=='procedural');
    $(this.screen,'[data-projectile-panel="glb"]').classList.toggle('hidden',this.mode!=='glb');
    $$ (this.screen,'[data-projectile-sound-mode]').forEach((button)=>{
      const [slot,mode]=button.dataset.projectileSoundMode.split(':');
      button.classList.toggle('selected',this.soundModes[slot]===mode);
    });
    $$ (this.screen,'[data-projectile-sound-panel]').forEach((panel)=>{
      const [slot,mode]=panel.dataset.projectileSoundPanel.split(':');
      panel.classList.toggle('hidden',this.soundModes[slot]!==mode);
    });
    this.updateActionButtons();
  }

  updateActionButtons(){
    const missingModel=this.mode==='glb'&&!this.assetFile;
    const missingSound=Object.keys(this.soundModes).some(
      (slot)=>this.soundModes[slot]==='mp3'&&!this.soundFiles[slot],
    );
    this.saveButton.disabled=missingModel||missingSound;
    Object.keys(this.soundButtons).forEach((slot)=>{
      this.soundButtons[slot].disabled=(
        this.soundModes[slot]==='mp3'&&!this.soundFiles[slot]
      );
    });
  }

  replace(object){
    if(this.preview){this.mount.remove(this.preview);dispose(this.preview);}
    this.preview=object;this.mount.add(object);
  }
  rebuild(){this.replace(createProjectile(this.design));}
  createImportedPreview(){
    if(!this.importedPreview)return null;
    const oriented=new THREE.Group();
    const model=cloneGlbPreview(this.importedPreview);
    model.rotation.set(
      THREE.MathUtils.degToRad(this.design.modelRotationX),
      THREE.MathUtils.degToRad(this.design.modelRotationY),
      THREE.MathUtils.degToRad(this.design.modelRotationZ),
    );
    model.scale.setScalar(this.design.scale);
    oriented.add(model);
    return oriented;
  }
  refreshImportedPreview(showStatus=false){
    const preview=this.createImportedPreview();
    if(!preview)return;
    this.replace(preview);
    if(showStatus){
      this.status.textContent=`Orientación aplicada: X ${this.design.modelRotationX}° · Y ${this.design.modelRotationY}° · Z ${this.design.modelRotationZ}°.`;
      this.status.dataset.state='ready';
    }
  }
  setMode(mode){
    this.mode=mode==='glb'?'glb':'procedural';this.sync();
    if(this.mode==='procedural')this.rebuild();
    else if(this.importedPreview)this.refreshImportedPreview();
  }
  setSoundMode(slot,mode){
    if(!Object.hasOwn(this.soundModes,slot)||!['procedural','mp3'].includes(mode))return;
    this.soundModes[slot]=mode;
    this.sync();
    this.status.textContent=mode==='mp3'
      ?this.soundFiles[slot]
        ?`${slot==='travel'?'Viaje':'Impacto'} MP3 listo para probar.`
        :`Elegí un MP3 para el sonido de ${slot==='travel'?'viaje':'impacto'}.`
      :`Sonido procedural de ${slot==='travel'?'viaje':'impacto'} listo para probar.`;
    this.status.dataset.state=mode==='mp3'&&this.soundFiles[slot]?'success':'ready';
  }
  async loadSoundFile(slot,file){
    if(!file||!Object.hasOwn(this.soundModes,slot))return;
    this.saveButton.disabled=true;
    this.soundButtons[slot].disabled=true;
    this.status.textContent=`Analizando el ${slot==='travel'?'viaje':'impacto'} MP3…`;
    this.status.dataset.state='ready';
    try{
      const stats=await inspectOneShotSample(file);
      this.soundModes[slot]='mp3';
      this.soundFiles[slot]=file;
      this.soundStats[slot]=stats;
      this.soundNames[slot].textContent=file.name;
      this.soundInfos[slot].textContent=[
        `${stats.duration.toFixed(2)} s`,
        `${stats.sampleRate} Hz`,
        stats.channels===1?'mono':`${stats.channels} canales`,
        stats.warnings.length?'revisar duración':'listo',
      ].join(' · ');
      this.sync();
      this.status.textContent=stats.warnings[0]
        ||`MP3 de ${slot==='travel'?'viaje':'impacto'} validado.`;
      this.status.dataset.state=stats.warnings.length?'warning':'success';
    }catch(error){
      console.error(error);
      this.soundFiles[slot]=null;
      this.soundStats[slot]=null;
      this.soundInputs[slot].value='';
      this.soundNames[slot].textContent='Elegir archivo MP3';
      this.soundInfos[slot].textContent='No se pudo validar el archivo';
      this.sync();
      this.status.textContent=error.message||'No se pudo abrir el MP3.';
      this.status.dataset.state='error';
    }
  }
  async testSound(slot){
    if(this.soundModes[slot]==='mp3'&&!this.soundFiles[slot]){
      this.status.textContent='Cargá un MP3 antes de probar el sonido.';
      this.status.dataset.state='warning';
      return;
    }
    this.soundButtons[slot].disabled=true;
    try{
      if(this.soundModes[slot]==='mp3'){
        await previewOneShotSample(this.soundFiles[slot],this.design,slot);
      }else{
        previewOneShot(this.design,slot);
      }
      this.status.textContent=`Reproduciendo el sonido de ${slot==='travel'?'viaje':'impacto'}.`;
      this.status.dataset.state='success';
    }catch(error){
      console.error(error);
      this.status.textContent=error.message||'No se pudo reproducir el sonido.';
      this.status.dataset.state='error';
    }finally{
      this.updateActionButtons();
    }
  }
  reset(){
    this.design=cloneDesign(PROJECTILES.find((item)=>item.id===this.template.value)||PROJECTILES[0]);
    this.sync();
    if(this.mode==='procedural')this.rebuild();
    else this.refreshImportedPreview(true);
  }
  clearWorkspace(){
    this.mode='procedural';this.template.value=PROJECTILES[0].id;this.design=cloneDesign(PROJECTILES[0]);
    this.assetFile=null;this.assetStats=null;this.fileInput.value='';this.fileName.textContent='Elegir archivo GLB';
    this.soundModes={travel:'procedural',impact:'procedural'};
    this.soundFiles={travel:null,impact:null};
    this.soundStats={travel:null,impact:null};
    Object.keys(this.soundInputs).forEach((slot)=>{
      this.soundInputs[slot].value='';
      this.soundNames[slot].textContent='Elegir archivo MP3';
      this.soundInfos[slot].textContent=slot==='travel'
        ?'Muestra corta reproducida al disparar'
        :'Muestra corta reproducida al impactar';
    });
    this.sync();this.rebuild();
  }
  async loadGlbFile(file){
    if(!file)return;
    try{
      const result=await loadCatalogGlb(file,'projectile');
      dispose(this.importedPreview);this.importedPreview=result.preview;this.assetFile=file;this.assetStats=result.stats;
      this.refreshImportedPreview();this.fileName.textContent=file.name;
      this.fileInfo.textContent=`${result.stats.meshes} mallas · ${formatGlbTriangles(result.stats.triangles)}`;
      this.updateActionButtons();
      this.status.textContent=result.stats.orientationWarning
        ?'GLB cargado. Ajustá la rotación X/Y/Z hasta que la punta mire hacia −Z.'
        :'GLB validado. Podés corregir su orientación con los controles X/Y/Z.';
      this.status.dataset.state=result.stats.orientationWarning?'warning':'success';
    }catch(error){
      this.assetFile=null;this.assetStats=null;
      this.fileInput.value='';
      this.fileName.textContent='Elegir archivo GLB';
      this.fileInfo.textContent='No se pudo validar el archivo';
      this.updateActionButtons();
      this.status.textContent=error.message||'No se pudo abrir el GLB.';this.status.dataset.state='error';
    }
  }
  async loadRecord(item){
    this.mode=item.profile?.mode||'procedural';this.design=cloneDesign(item.profile?.design||PROJECTILES[0]);
    this.assetFile=item.assetFile||null;this.assetStats=item.profile?.asset||null;
    this.soundModes={
      travel:item.profile?.sounds?.travel?.mode==='mp3'?'mp3':'procedural',
      impact:item.profile?.sounds?.impact?.mode==='mp3'?'mp3':'procedural',
    };
    this.soundFiles={
      travel:item.audioFiles?.travel||null,
      impact:item.audioFiles?.impact||null,
    };
    this.soundStats={
      travel:item.profile?.sounds?.travel||null,
      impact:item.profile?.sounds?.impact||null,
    };
    Object.keys(this.soundInputs).forEach((slot)=>{
      this.soundInputs[slot].value='';
      this.soundNames[slot].textContent='Elegir archivo MP3';
      this.soundInfos[slot].textContent=slot==='travel'
        ?'Muestra corta reproducida al disparar'
        :'Muestra corta reproducida al impactar';
    });
    this.sync();
    if(this.mode==='glb'&&this.assetFile)await this.loadGlbFile(this.assetFile);else this.rebuild();
    for(const slot of Object.keys(this.soundModes)){
      if(this.soundModes[slot]==='mp3'&&this.soundFiles[slot]){
        await this.loadSoundFile(slot,this.soundFiles[slot]);
      }
    }
  }
  open(){this.active=true;this.callbacks.onOpen?.();requestAnimationFrame(()=>this.resize());}
  close(){this.active=false;this.callbacks.onClose?.();}
  async save(){
    const id=cleanId($(this.screen,'#projectile-editor-id').value);$(this.screen,'#projectile-editor-id').value=id;
    if(this.mode==='glb'&&!this.assetFile){this.status.textContent='Cargá un GLB antes de guardar.';this.status.dataset.state='warning';return;}
    const missingSlot=Object.keys(this.soundModes).find(
      (slot)=>this.soundModes[slot]==='mp3'&&!this.soundFiles[slot],
    );
    if(missingSlot){
      this.status.textContent=`Cargá un MP3 para el sonido de ${missingSlot==='travel'?'viaje':'impacto'}.`;
      this.status.dataset.state='warning';
      return;
    }
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        mode:this.mode,
        design:cloneDesign(this.design),
        assetFile:this.assetFile,
        assetStats:this.assetStats,
        soundModes:{...this.soundModes},
        soundFiles:{...this.soundFiles},
        soundStats:{...this.soundStats},
      });
      this.status.textContent=result?.message||'Proyectil guardado.';this.status.dataset.state='success';
    }catch(error){this.status.textContent=error.message||'No se pudo guardar.';this.status.dataset.state='error';}
    finally{this.updateActionButtons();}
  }
  resize(){
    if(!this.active)return;const width=this.canvas.clientWidth,height=this.canvas.clientHeight;if(!width||!height)return;
    this.renderer.setSize(width,height,false);this.camera.aspect=width/height;this.camera.updateProjectionMatrix();
  }
  animate(){
    requestAnimationFrame(()=>this.animate());if(!this.active)return;this.resize();this.controls.update();
    if(this.preview)this.preview.rotation.z+=.006;this.renderer.render(this.scene,this.camera);
  }
}
